import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Container, Row, Stack } from 'react-bootstrap';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import {
    getFeaturedScript,
    getFeaturedDownloadURL,
    FeaturedError,
    FeaturedScriptDetail,
} from '../utils/Featured';
import { showToast } from '../utils/Toasts';

import { LoadingRing } from '../components/LoadingRing';
import { LoadingText } from '../components/LoadingText';
import AltViewer from '../components/AltViewer';
import PdfViewer from '../components/PdfViewer';

import useUserStore from '../stores/User';

/**
 * Public, no-login view of a single curated script. Everything on this page
 * comes from the featured-script edge function, which only resolves scripts
 * that an approver has explicitly featured.
 */
function FeaturedScriptView() {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    // Set when AuthGuard forwarded a /database/script/<id> link here on a hunch.
    const cameFromDatabase = searchParams.get('from') === 'database';
    const isExtraLarge = useMediaQuery({ query: '(min-width: 1200px)' });
    const isLoggedIn = useUserStore((state) => state.isLoggedIn());

    const [script, setScript] = useState<FeaturedScriptDetail | null>(null);
    const [error, setError] = useState<FeaturedError | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            const { script: found, error: loadError } = await getFeaturedScript(slug!);
            if (cancelled) return;

            setScript(found);
            setError(loadError);
            setLoading(false);
        };

        if (slug) load();

        return () => {
            cancelled = true;
        };
    }, [slug]);

    useEffect(() => {
        if (!script) return;

        const previousTitle = document.title;
        document.title = `${script.headline} — Script Hive`;

        return () => {
            document.title = previousTitle;
        };
    }, [script]);

    // The signed URL is short-lived; hand PdfViewer a resolver so an expired link
    // is replaced without the visitor needing to reload the page.
    const refreshUrl = useCallback(async () => {
        const { script: refreshed } = await getFeaturedScript(slug!);
        return refreshed?.url ?? null;
    }, [slug]);

    const handleDownload = async () => {
        setDownloading(true);
        const url = await getFeaturedDownloadURL(slug!);
        setDownloading(false);

        if (!url) {
            showToast('error', 'Sorry, that download is unavailable right now.');
            return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    };

    if (loading) {
        return (
            <div className="hive-container d-flex">
                <LoadingRing />
            </div>
        );
    }

    // The forwarded link wasn't a featured script after all, so resume the normal
    // logged-out flow: prompt for login, then land on the script they asked for.
    if (!script && cameFromDatabase && error === 'notFound') {
        return <Navigate to={`/DatabaseLogin?redirectTo=/database/script/${slug}`} replace />;
    }

    // Reached by file_id (a forwarded database link) — swap the address bar over to
    // the readable slug so what people copy and share is the canonical URL.
    if (script && slug !== script.slug) {
        return <Navigate to={`/featured/${script.slug}`} replace />;
    }

    if (!script) {
        return (
            <Container fluid className="px-3 px-sm-5 my-5">
                <Row>
                    <Col xs={12} className="text-center py-5">
                        <i className="bi bi-file-earmark-x text-muted" style={{ fontSize: '4rem' }}></i>
                        <h1 className="fs-4 mt-3">
                            {error === 'rateLimited' ? 'Just a moment' : 'Script not available'}
                        </h1>
                        <p className="text-muted">
                            {error === 'rateLimited'
                                ? "We've seen a lot of requests from your network. Please try again in a minute."
                                : error === 'unavailable'
                                    ? "We couldn't load this script right now. Please try again shortly."
                                    : "This script isn't part of our featured collection. It may still be in the full library."}
                        </p>
                        <Stack direction="horizontal" gap={3} className="justify-content-center mt-4 flex-wrap">
                            <Link to="/featured">
                                <Button variant="secondary">See featured scripts</Button>
                            </Link>
                            <Link to={isLoggedIn ? '/database' : '/databaseLogin'}>
                                <Button variant="primary">Browse the full library</Button>
                            </Link>
                        </Stack>
                    </Col>
                </Row>
            </Container>
        );
    }

    const details: [string, string | null][] = [
        ['Episode', script.episode],
        ['Draft Date', script.draftDate],
        ['Document Type', script.documentType],
        ['Version', script.draft],
        ['Alternate Titles', script.alternateTitles],
        [
            'Industry Lists and Accolades',
            [script.industryList, script.year].filter(Boolean).join(', ') || null,
        ],
    ];

    return (
        <div className="hive-container d-flex">
            <div className="d-flex justify-content-between w-100">
                <div className="mx-sm-5 w-100 pb-1 pb-md-4">
                    <Container fluid className="p-3" style={{ height: 'calc(100vh - 120px)' }}>
                        <Row className={`g-3 ${isExtraLarge ? 'h-100' : ''} min-zero`} xs={2}>
                            <Col
                                xl={6}
                                xs={12}
                                className="pe-lg-3 d-flex flex-column overflow-hidden h-100 min-zero order-last order-xl-0 mb-4 mb-lg-0"
                            >
                                <Row>
                                    <Col xs={12}>
                                        <span className="badge bg-primary mb-2">Featured Script</span>
                                        <h1 className="fs-5 fw-bold text-break">{script.headline}</h1>
                                        <h2 className="fs-6 mb-2 text-break">
                                            {`Written by ${script.writers || 'Unlisted'}`}
                                        </h2>
                                        {script.headline !== script.scriptTitle && script.scriptTitle && (
                                            <p className="text-muted small mb-2">{script.scriptTitle}</p>
                                        )}
                                    </Col>
                                </Row>

                                {script.blurb && (
                                    <Row>
                                        <Col xs={12}>
                                            <p className="text-muted text-break mt-2" style={{ whiteSpace: 'pre-line' }}>
                                                {script.blurb}
                                            </p>
                                        </Col>
                                    </Row>
                                )}

                                <Row xs={1} lg={2}>
                                    {details.map(([label, value]) =>
                                        value ? (
                                            <Col md={6} xs={12} className="min-zero" key={label}>
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">{label}</h3>
                                                <p className="text-break">{value}</p>
                                            </Col>
                                        ) : null
                                    )}
                                </Row>

                                <Row className={isExtraLarge ? 'mt-auto' : 'mt-4'}>
                                    <Col xs={12}>
                                        <p className="text-muted small mb-2">
                                            Script Hive is a free library of over 27,000 professional film and TV
                                            screenplays, free for screenwriters to study.{' '}
                                            {isLoggedIn
                                                ? 'Head to the database to read the rest.'
                                                : 'Create a free account to read the rest.'}
                                        </p>
                                    </Col>
                                </Row>

                                <Stack
                                    direction="horizontal"
                                    gap={3}
                                    className={`${isExtraLarge ? '' : 'mt-3 mb-2 mb-md-5'} d-flex flex-wrap`}
                                >
                                    <Link to={isLoggedIn ? '/database' : '/register'}>
                                        <Button variant="primary">
                                            <i className="bi bi-collection me-2"></i>
                                            {isLoggedIn ? 'Browse the library' : 'Get free access'}
                                        </Button>
                                    </Link>
                                    <Link to="/featured">
                                        <Button variant="secondary">
                                            <i className="bi bi-star me-2"></i>More featured scripts
                                        </Button>
                                    </Link>
                                    {script.allowDownload && (
                                        <Button variant="outline-primary" disabled={downloading} onClick={handleDownload}>
                                            {downloading ? (
                                                <LoadingText text="Loading" className="mb-0" />
                                            ) : (
                                                <span>
                                                    <i className="bi bi-file-earmark-arrow-down-fill me-2"></i>Download
                                                </span>
                                            )}
                                        </Button>
                                    )}
                                </Stack>
                            </Col>

                            <Col
                                xl={6}
                                xs={12}
                                className="overflow-hidden h-100 d-flex flex-column min-zero order-first order-xl-1"
                            >
                                <div className="border rounded overflow-hidden flex-grow-1 pdf-viewer">
                                    {script.isPdf ? (
                                        <PdfViewer
                                            fileUrl={script.url}
                                            fileName={script.headline}
                                            fileId={script.slug}
                                            onRefreshUrl={refreshUrl}
                                            allowDownload={script.allowDownload}
                                            onDownload={handleDownload}
                                            downloading={downloading}
                                        />
                                    ) : (
                                        <AltViewer fileName={script.headline} />
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </div>
        </div>
    );
}

export default FeaturedScriptView;
